import { ChevronLeft } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import type { Contact, DealCard, LeadSource, Sale } from "../types";
import { fullName, usePermissions, useStage } from "./hooks";
import { NativeSelect } from "./shared";
import { phoneKey } from "./leadLogic";

const SOURCES: LeadSource[] = [
  "phone",
  "facebook",
  "instagram",
  "site",
  "instagram_direct",
  "messenger",
  "referral",
  "repeat",
  "walk_in",
  "other",
];

/** Manual lead entry (docs/design/NewLeadM.dc.html): contact + deal at "new" */
export const NewLeadPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider();
  const stage = useStage();
  const permissions = usePermissions();
  const { identity } = useGetIdentity();
  const canAssign = permissions.includes("leads.transfer");
  const [phone, setPhone] = useState("+380 ");
  const [name, setName] = useState("");
  // no default: the source is required, every sale is counted by it
  const [source, setSource] = useState<LeadSource | "">("");
  const [calledNumber, setCalledNumber] = useState("");
  const [rooms, setRooms] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [salesId, setSalesId] = useState("");
  const [saving, setSaving] = useState(false);
  const key = phoneKey(phone);

  // Same number already in the base: warn, but let the manager decide
  const { data: sameNumber = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: { "phone_digits@ilike": `%${key}%` },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: key.length === 9 },
  );
  const duplicate = sameNumber[0];
  const { data: duplicateDeal = [] } = useGetList<DealCard>(
    "deal_cards",
    {
      filter: { contact_id: duplicate?.id },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: duplicate != null },
  );
  const { data: sales = [] } = useGetList<Sale>(
    "sales",
    { pagination: { page: 1, perPage: 100 } },
    { enabled: canAssign },
  );

  const submit = async () => {
    setSaving(true);
    try {
      const [first_name, ...rest] = name.trim().split(/\s+/);
      const owner = canAssign && salesId ? Number(salesId) : identity?.id;
      const { data: contact } = await dataProvider.create<Contact>("contacts", {
        data: {
          first_name: first_name || translate("crm.funnel.new.no_name"),
          last_name: rest.join(" "),
          phone_jsonb: [{ number: phone.trim(), type: "Work" }],
          email_jsonb: [],
          source,
          called_number:
            source === "phone" && calledNumber.trim()
              ? calledNumber.trim()
              : null,
          rooms_wanted: rooms,
          sales_id: owner,
          first_seen: new Date().toISOString(),
          last_seen: new Date().toISOString(),
        },
      });
      const { data: deal } = await dataProvider.create("deals", {
        data: {
          name: [contact.first_name, contact.last_name]
            .filter(Boolean)
            .join(" "),
          stage: "new",
          contact_ids: [contact.id],
          sales_id: owner,
          index: 0,
        },
      });
      if (comment.trim()) {
        await dataProvider.create("contact_notes", {
          data: {
            contact_id: contact.id,
            text: comment.trim(),
            date: new Date().toISOString(),
          },
        });
      }
      notify("crm.funnel.new.created");
      navigate(`/funnel/${deal.id}`);
    } catch {
      notify("ra.notification.http_error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const label =
    "flex flex-col gap-1 text-sm font-semibold text-muted-foreground";
  const back = (
    <Link to="/funnel" className="inline-flex items-center gap-1 text-primary">
      <ChevronLeft className="size-5" />
      <span className="text-xl font-bold text-foreground">
        {translate("crm.funnel.new.title")}
      </span>
    </Link>
  );

  const form = (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-4">
      {isMobile ? null : back}
      <label className={label}>
        {translate("crm.funnel.new.phone")}
        <Input
          type="tel"
          inputMode="tel"
          autoFocus
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
        />
      </label>
      {duplicate ? (
        <div className="rounded-xl border border-honey bg-honey/15 px-4 py-3 text-sm text-warning">
          <b>{translate("crm.funnel.new.attention")}</b>{" "}
          {translate("crm.funnel.new.duplicate", {
            name: fullName(duplicate),
            stage: stage(duplicateDeal[0]?.stage)?.label ?? "—",
          })}{" "}
          {duplicateDeal[0] ? (
            <Link
              to={`/funnel/${duplicateDeal[0].id}`}
              className="font-bold underline"
            >
              {translate("crm.funnel.new.open")}
            </Link>
          ) : null}
        </div>
      ) : null}
      <label className={label}>
        {translate("crm.funnel.new.name")}
        <Input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={translate("crm.funnel.new.name_placeholder")}
        />
      </label>
      <label className={label}>
        {translate("crm.funnel.new.source")}
        <NativeSelect
          value={source}
          onChange={(event) => setSource(event.target.value as LeadSource)}
          className={cn(!source && "text-muted-foreground")}
        >
          <option value="" disabled>
            {translate("crm.funnel.new.source_pick")}
          </option>
          {SOURCES.map((value) => (
            <option key={value} value={value}>
              {translate(`resources.contacts.sources.${value}`)}
            </option>
          ))}
        </NativeSelect>
      </label>
      {source === "phone" ? (
        <label className={label}>
          {translate("crm.funnel.new.called_number")}
          <Input
            inputMode="tel"
            value={calledNumber}
            onChange={(event) => setCalledNumber(event.target.value)}
            placeholder="+380…"
          />
        </label>
      ) : null}
      <div className={label}>
        {translate("crm.funnel.new.rooms")}
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map((count) => (
            <button
              key={count}
              type="button"
              aria-pressed={rooms === count}
              onClick={() => setRooms(rooms === count ? null : count)}
              className={cn(
                "rounded-xl border py-3 text-base font-semibold text-foreground",
                rooms === count
                  ? "border-primary bg-accent text-primary"
                  : "bg-card",
              )}
            >
              {translate("crm.funnel.new.rooms_option", { count })}
            </button>
          ))}
        </div>
      </div>
      {canAssign ? (
        <label className={label}>
          {translate("crm.funnel.new.manager")}
          <NativeSelect
            value={salesId}
            onChange={(event) => setSalesId(event.target.value)}
          >
            <option value="">{identity?.fullName}</option>
            {sales
              .filter((sale) => !sale.disabled && sale.id !== identity?.id)
              .map((sale) => (
                <option key={sale.id} value={sale.id}>
                  {sale.first_name} {sale.last_name}
                </option>
              ))}
          </NativeSelect>
        </label>
      ) : null}
      <label className={label}>
        {translate("crm.funnel.new.comment")}
        <Textarea
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={translate("crm.funnel.new.comment_placeholder")}
        />
      </label>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          size="lg"
          onClick={submit}
          disabled={saving || key.length < 9 || !source}
        >
          {translate("crm.funnel.new.submit")}
        </Button>
        <Button
          type="button"
          size="lg"
          variant="outline"
          onClick={() => navigate("/funnel")}
        >
          {translate("ra.action.cancel")}
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>{back}</MobileHeader>
        <MobileContent>{form}</MobileContent>
      </>
    );
  }
  return form;
};

NewLeadPage.path = "/funnel/new";
